import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY,
  invalidateQueriesTransactions,
} from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  GetTransactionListResponse,
  GetTransactionResponse,
  GetTransactionV1SrTransactionsIdGetParams,
  GetTransactionsListV1SrTransactionsGetParams,
  TransactionSetAwaitPaymentStatusRequest,
  TransactionSetCompletedStatusRequest,
  TransactionSetRejectStatusRequest,
} from 'api';

export const CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY = 'vestra/issuer-current-transaction-by-id';

export const useIssuerTransactionByIdQuery = (
  payload: GetTransactionV1SrTransactionsIdGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  return useQuery<GetTransactionResponse>({
    queryKey: [CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY, JSON.stringify(payload), registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTransactionV1SrTransactionsIdGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTransactionArchiveByIdMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.archiveTransactionV1SrArchivingTransactionsIdPost({ id }),
        {
          loading: 'Transaction archiving...',
          success: 'Transaction archived!',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({
        updateDividend: false,
      });
    },
  });
};

export const useTransactionsListQuery = (
  payload: GetTransactionsListV1SrTransactionsGetParams,
  disabled = false,
  register_id: number,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetTransactionListResponse>({
    queryKey: [
      CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY,
      register_id,
      JSON.stringify(data),
      // ...Object.entries(data).map(obj => obj[1]),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getTransactionsListV1SrTransactionsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
    refetchInterval: 1000 * 60, //1min
  });
};

export const useTransactionSetCompletedMutation = () => {
  return useMutation({
    mutationFn: async (payload: TransactionSetCompletedStatusRequest) => {
      const response = await api.v1.transactionSetCompletedV1SrTransactionsStatusCompletedPatch(
        payload,
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const useTransactionAwaitingPaymentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: TransactionSetAwaitPaymentStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transactionSetAwaitPaymentV1SrTransactionsStatusAwaitPaymentPatch(payload),
        {
          loading: 'Updating transaction...',
          success: 'Transaction updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const useTransactionSetRejectedMutation = () => {
  return useMutation({
    mutationFn: async (payload: TransactionSetRejectStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transactionSetRejectV1SrTransactionsStatusRejectedPatch(payload),
        {
          loading: 'Updating transaction...',
          success: 'Transaction updated',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};
